import { useState, useCallback, useEffect, useRef } from 'react';
import { EntityCountCard } from '@/components/EntityCountCard';
import { SyncHistory } from '@/components/SyncHistory';
import { SyncButton } from '@/components/SyncButton';
import { SyncProgressIndicator } from '@/components/SyncProgressIndicator';
import { EntityMappingTable } from '@/components/EntityMappingTable';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { useSyncStatus } from '@/hooks/useSyncStatus';
import { useSyncStatusPolling } from '@/hooks/useSyncStatusPolling';
import { useManualSync } from '@/hooks/useManualSync';
import { useRefreshCounts } from '@/hooks/useRefreshCounts';
import { useEntityMappings } from '@/hooks/useEntityMappings';
import { useXeroAuth } from '@/hooks/useXeroAuth';
import { useHighLevelAuth } from '@/hooks/useHighLevelAuth';
import type { SyncHistoryEntry, SyncStatus } from '@/types';
import {
  Users,
  Package,
  FileText,
  AlertCircle,
  AlertTriangle,
  Clock,
  RefreshCw,
  Info,
  Link2,
} from 'lucide-react';

// Check if entity counts are stale (> 5 minutes old)
const STALE_THRESHOLD_MS = 5 * 60 * 1000; // 5 minutes

function areCountsStale(syncStatus: SyncStatus | undefined): boolean {
  if (!syncStatus?.entityCounts) return true;

  const now = Date.now();
  const entityTypes = ['contacts', 'products', 'invoices'] as const;

  for (const entityType of entityTypes) {
    const counts = syncStatus.entityCounts[entityType];
    if (!counts) continue;

    // Check both HighLevel and Xero updated timestamps
    const hlUpdatedAt = counts.highlevel?.updatedAt;
    const xeroUpdatedAt = counts.xero?.updatedAt;

    // If either is missing or stale, we need to refresh
    if (!hlUpdatedAt || (now - new Date(hlUpdatedAt).getTime()) > STALE_THRESHOLD_MS) {
      return true;
    }
    if (!xeroUpdatedAt || (now - new Date(xeroUpdatedAt).getTime()) > STALE_THRESHOLD_MS) {
      return true;
    }
  }

  return false;
}

export function DashboardPage() {
  const { isConnected: xeroConnected } = useXeroAuth();
  const { isConnected: highlevelConnected } = useHighLevelAuth();
  const bothConnected = xeroConnected && highlevelConnected;

  // Only fetch sync data when both CRM and Xero are connected
  const { jobs, isLoading: isLoadingStats } = useSyncStatus({ enabled: bothConnected });
  const { syncStatus, isLoading: isLoadingSyncStatus } = useSyncStatusPolling({ enabled: bothConnected });
  const { triggerSync, isLoading: isSyncing, activeSyncs, handleSyncComplete } = useManualSync();
  const {
    refreshCounts,
    refreshEntityTypeCounts,
    matchContacts,
    isLoading: isRefreshingCounts,
    isMatchingContacts,
  } = useRefreshCounts();

  // Track which entity type is being refreshed
  const [refreshingEntityType, setRefreshingEntityType] = useState<'contact' | 'product' | 'invoice' | null>(null);

  // Wrapper to track which entity is being refreshed
  const handleRefreshEntityType = async (entityType: 'contact' | 'product' | 'invoice') => {
    setRefreshingEntityType(entityType);
    try {
      await refreshEntityTypeCounts(entityType);
    } finally {
      setRefreshingEntityType(null);
    }
  };

  // Track if we've already auto-refreshed to avoid duplicate calls
  const hasAutoRefreshed = useRef(false);

  // Auto-refresh counts on screen load if stale (> 5 minutes)
  useEffect(() => {
    if (!bothConnected || isLoadingSyncStatus || hasAutoRefreshed.current || isRefreshingCounts) {
      return;
    }

    if (syncStatus && areCountsStale(syncStatus)) {
      console.log('[DashboardPage] Counts are stale, auto-refreshing...');
      hasAutoRefreshed.current = true;
      refreshCounts();
    }
  }, [bothConnected, syncStatus, isLoadingSyncStatus, isRefreshingCounts, refreshCounts]);

  // Create callbacks for each entity type's sync completion
  // These trigger a count refresh for just that entity type after sync completes
  const handleContactsSyncComplete = useCallback((entry: SyncHistoryEntry) => {
    handleSyncComplete('contacts', entry);
    // Refresh only contact counts after sync completes (more efficient)
    setTimeout(() => refreshEntityTypeCounts('contact'), 1000);
  }, [handleSyncComplete, refreshEntityTypeCounts]);

  const handleProductsSyncComplete = useCallback((entry: SyncHistoryEntry) => {
    handleSyncComplete('products', entry);
    // Refresh only product counts after sync completes (more efficient)
    setTimeout(() => refreshEntityTypeCounts('product'), 1000);
  }, [handleSyncComplete, refreshEntityTypeCounts]);

  const handleInvoicesSyncComplete = useCallback((entry: SyncHistoryEntry) => {
    handleSyncComplete('invoices', entry);
    // Refresh only invoice counts after sync completes (more efficient)
    setTimeout(() => refreshEntityTypeCounts('invoice'), 1000);
  }, [handleSyncComplete, refreshEntityTypeCounts]);

  // Full Sync toggle - when true, syncs ALL items; when false (default), only syncs unmapped items
  const [fullSyncMode, setFullSyncMode] = useState(false);

  const [selectedEntity, setSelectedEntity] = useState<
    'contact' | 'product' | 'invoice' | undefined
  >(undefined);
  const { mappings, conflicts, resolveConflict } =
    useEntityMappings(selectedEntity, { enabled: bothConnected });

  if (!bothConnected) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-yellow-800">
                Connection Required
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                Please connect both Xero and your CRM accounts to view the sync dashboard.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingStats || isLoadingSyncStatus) {
    return <LoadingSpinner />;
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">
          Sync Dashboard
        </h1>
        <p className="mt-1 text-sm text-gray-500">
          Monitor sync status and manually trigger sync operations
        </p>
      </div>

      {/* Manual Sync Information Box with Full Sync Toggle */}
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <div className="flex items-start">
          <Info className="h-5 w-5 text-blue-400 mt-0.5" />
          <div className="ml-3 flex-1">
            <h3 className="text-sm font-medium text-blue-800">
              How Manual Sync Works
            </h3>
            <div className="mt-2 text-sm text-blue-700 space-y-1">
              <p>
                <strong>Smart Sync (Default):</strong> Only syncs items that haven't been synced before. Faster and more efficient for regular use.
              </p>
              <p>
                <strong>Full Sync:</strong> Re-syncs ALL items, including those already synced. Use this to update existing records or fix sync issues.
              </p>
            </div>

            {/* Full Sync Toggle */}
            <div className="mt-4 flex items-center">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={fullSyncMode}
                  onChange={(e) => setFullSyncMode(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                <span className="ms-3 text-sm font-medium text-blue-800">
                  {fullSyncMode ? 'Full Sync Mode' : 'Smart Sync Mode'}
                </span>
              </label>
              <span className="ml-2 text-xs text-blue-600">
                {fullSyncMode
                  ? '(Re-sync all items including already synced)'
                  : '(Only sync new/unmapped items)'}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Sync Controls Grid - 3 columns for each entity type */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Contacts Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <Users className="h-5 w-5 text-gray-700" />
            <h2 className="text-lg font-medium text-gray-900">
              Contacts
            </h2>
          </div>

          <SyncProgressIndicator
            entityType="contacts"
            status={syncStatus?.syncState.contacts}
            activeSyncHistoryId={activeSyncs.contacts}
            onSyncComplete={handleContactsSyncComplete}
          />

          <div className="mt-4 space-y-2">
            <SyncButton
              entityType="contacts"
              direction="to_xero"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
            <SyncButton
              entityType="contacts"
              direction="from_xero"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
            <SyncButton
              entityType="contacts"
              direction="bidirectional"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
          </div>
        </div>

        {/* Products Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <Package className="h-5 w-5 text-gray-700" />
            <h2 className="text-lg font-medium text-gray-900">
              Products
            </h2>
          </div>

          <SyncProgressIndicator
            entityType="products"
            status={syncStatus?.syncState.products}
            activeSyncHistoryId={activeSyncs.products}
            onSyncComplete={handleProductsSyncComplete}
          />

          <div className="mt-4 space-y-2">
            <SyncButton
              entityType="products"
              direction="to_xero"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
            <SyncButton
              entityType="products"
              direction="from_xero"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
            <SyncButton
              entityType="products"
              direction="bidirectional"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
          </div>
        </div>

        {/* Invoices Card */}
        <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
          <div className="flex items-center space-x-2 mb-4">
            <FileText className="h-5 w-5 text-gray-700" />
            <h2 className="text-lg font-medium text-gray-900">
              Invoices
            </h2>
          </div>

          <SyncProgressIndicator
            entityType="invoices"
            status={syncStatus?.syncState.invoices}
            activeSyncHistoryId={activeSyncs.invoices}
            onSyncComplete={handleInvoicesSyncComplete}
          />

          <div className="mt-4 space-y-2">
            <SyncButton
              entityType="invoices"
              direction="to_xero"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
            <SyncButton
              entityType="invoices"
              direction="from_xero"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
            <SyncButton
              entityType="invoices"
              direction="bidirectional"
              onSync={triggerSync}
              isLoading={isSyncing}
              fullSync={fullSyncMode}
            />
          </div>
        </div>
      </div>

      {/* Queue Status - shows pending/processing/failed counts */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Queue Status
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <Clock className="h-8 w-8 text-yellow-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {syncStatus?.queueStatus.pending || 0}
              </p>
              <p className="text-sm text-gray-600">Pending</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <div className="h-8 w-8 rounded-full bg-blue-100 flex items-center justify-center">
                <div className="h-4 w-4 rounded-full bg-blue-600 animate-pulse" />
              </div>
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {syncStatus?.queueStatus.processing || 0}
              </p>
              <p className="text-sm text-gray-600">Processing</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="flex-shrink-0">
              <AlertCircle className="h-8 w-8 text-red-500" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-900">
                {syncStatus?.queueStatus.failed || 0}
              </p>
              <p className="text-sm text-gray-600">Failed</p>
            </div>
          </div>
        </div>
      </div>

      {/* Entity Counts - GHL vs Xero */}
      <div className="space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <h2 className="text-lg font-medium text-gray-900">
            Entity Counts
          </h2>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => matchContacts()}
              disabled={isMatchingContacts || isRefreshingCounts}
              className="inline-flex items-center px-3 py-1.5 border border-blue-300 shadow-sm text-sm font-medium rounded-md text-blue-700 bg-blue-50 hover:bg-blue-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Find matching contacts between HighLevel and Xero and create sync mappings"
            >
              <Link2 className={`h-4 w-4 mr-1.5 ${isMatchingContacts ? 'animate-pulse' : ''}`} />
              {isMatchingContacts ? 'Matching...' : 'Match Contacts'}
            </button>
            <button
              onClick={() => refreshCounts()}
              disabled={isRefreshingCounts || isMatchingContacts}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 mr-1.5 ${isRefreshingCounts && !refreshingEntityType ? 'animate-spin' : ''}`} />
              {isRefreshingCounts && !refreshingEntityType ? 'Refreshing...' : 'Refresh All'}
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          {/* Contacts Card */}
          <div className="relative">
            <EntityCountCard
              title="Contacts"
              counts={syncStatus?.entityCounts?.contacts || {
                highlevel: { synced: 0, unsynced: 0, total: 0 },
                xero: { synced: 0, unsynced: 0, total: 0 },
              }}
              icon={Users}
            />
            <button
              onClick={() => handleRefreshEntityType('contact')}
              disabled={isRefreshingCounts || isMatchingContacts}
              className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh contact counts"
            >
              <RefreshCw className={`h-4 w-4 ${refreshingEntityType === 'contact' ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {/* Products Card */}
          <div className="relative">
            <EntityCountCard
              title="Products"
              counts={syncStatus?.entityCounts?.products || {
                highlevel: { synced: 0, unsynced: 0, total: 0 },
                xero: { synced: 0, unsynced: 0, total: 0 },
              }}
              icon={Package}
            />
            <button
              onClick={() => handleRefreshEntityType('product')}
              disabled={isRefreshingCounts || isMatchingContacts}
              className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh product counts"
            >
              <RefreshCw className={`h-4 w-4 ${refreshingEntityType === 'product' ? 'animate-spin' : ''}`} />
            </button>
          </div>
          {/* Invoices Card */}
          <div className="relative">
            <EntityCountCard
              title="Invoices"
              counts={syncStatus?.entityCounts?.invoices || {
                highlevel: { synced: 0, unsynced: 0, total: 0 },
                xero: { synced: 0, unsynced: 0, total: 0 },
              }}
              icon={FileText}
            />
            <button
              onClick={() => handleRefreshEntityType('invoice')}
              disabled={isRefreshingCounts || isMatchingContacts}
              className="absolute top-3 right-3 p-1.5 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Refresh invoice counts"
            >
              <RefreshCw className={`h-4 w-4 ${refreshingEntityType === 'invoice' ? 'animate-spin' : ''}`} />
            </button>
          </div>
        </div>
      </div>

      {/* Conflicts Alert */}
      {conflicts.length > 0 && (
        <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4">
          <div className="flex items-start">
            <AlertTriangle className="h-5 w-5 text-yellow-400" />
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-yellow-800">
                Sync Conflicts Detected
              </h3>
              <p className="mt-1 text-sm text-yellow-700">
                There are {conflicts.length} conflicts that need your attention.
                Review them in the Entity Mappings section below.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Sync History */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="text-lg font-medium text-gray-900 mb-4">
          Recent Sync History
        </h2>
        <SyncHistory jobs={jobs} />
      </div>

      {/* Entity Mappings */}
      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Entity Mappings
          </h2>
          <div className="flex space-x-2">
            <button
              onClick={() => setSelectedEntity(undefined)}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                !selectedEntity
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              All
            </button>
            <button
              onClick={() => setSelectedEntity('contact')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedEntity === 'contact'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Contacts
            </button>
            <button
              onClick={() => setSelectedEntity('product')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedEntity === 'product'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Products
            </button>
            <button
              onClick={() => setSelectedEntity('invoice')}
              className={`rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                selectedEntity === 'invoice'
                  ? 'bg-primary-100 text-primary-700'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Invoices
            </button>
          </div>
        </div>
        <EntityMappingTable
          mappings={mappings}
          onResolveConflict={(mappingId, resolution) =>
            resolveConflict({ mappingId, resolution })
          }
        />
      </div>
    </div>
  );
}
